import { IconPlus } from "@tabler/icons-react";
import { useState } from "react";
import { z } from "zod";
import { useCecos, useClavesSat, useCuentas, useIndicadoresImpuesto } from "@/features/catalogos/hooks/use-catalogos";
import { useAgregarLineaManual } from "@/features/prorrateo/hooks/use-prorrateo-mutations";
import { Button, Card, Group, Heading, Input, NumberInput, Select, Stack, Text } from "@/components/ui";

const schema = z.object({
  claveSat: z.string().min(1, "Selecciona una clave SAT"),
  descripcion: z.string().trim().min(1, "Descripción obligatoria"),
  centroDeCostoId: z.string().uuid("Selecciona un centro de costo"),
  cuentaContableId: z.string().uuid("Selecciona una cuenta"),
  importe: z.number().positive("El importe debe ser mayor a cero"),
  indicador: z.string().min(1, "Selecciona un indicador de impuesto"),
});

type FormValues = z.infer<typeof schema>;
type FormErrors = Partial<Record<keyof FormValues, string>>;

/** Form controlado a mano (reemplaza el form de Mantine + `zod4Resolver` · patrón `LoginForm`). */
export function NuevaLineaManualForm({ solicitudId }: { solicitudId: string }) {
  const { data: cecos } = useCecos();
  const { data: cuentas } = useCuentas();
  const { data: claves } = useClavesSat();
  const { data: indicadores } = useIndicadoresImpuesto();
  const agregar = useAgregarLineaManual(solicitudId);

  const [values, setValues] = useState<FormValues>({
    claveSat: claves[0]?.clave ?? "",
    descripcion: "",
    centroDeCostoId: cecos[0]?.id ?? "",
    cuentaContableId: cuentas[0]?.id ?? "",
    importe: 0,
    indicador: indicadores[0]?.codigo ?? "",
  });
  const [errors, setErrors] = useState<FormErrors>({});

  const set = <K extends keyof FormValues>(k: K, v: FormValues[K]) => setValues((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = schema.safeParse(values);
    if (!parsed.success) {
      const fieldErrors: FormErrors = {};
      for (const issue of parsed.error.issues) {
        const key = issue.path[0] as keyof FormValues;
        if (!fieldErrors[key]) fieldErrors[key] = issue.message;
      }
      setErrors(fieldErrors);
      return;
    }
    setErrors({});
    await agregar.mutateAsync(parsed.data);
    setValues((v) => ({ ...v, descripcion: "", importe: 0 }));
  };

  return (
    <Card padding="lg">
      <Stack gap="md">
        <div>
          <Heading order={5}>Agregar línea manual</Heading>
          <Text size="sm" c="dimmed" className="mt-0.5">
            Para conceptos adicionales · numeración del sistema en múltiplos de 10.
          </Text>
        </div>
        <form onSubmit={onSubmit} noValidate>
          <Stack gap="sm">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-5">
              <Select label="Clave SAT" options={claves.map((c) => ({ value: c.clave, label: c.clave }))} searchable value={values.claveSat} onValueChange={(v) => set("claveSat", v)} error={errors.claveSat} />
              <Input
                label="Descripción"
                wrapperClassName="sm:col-span-2"
                value={values.descripcion}
                onChange={(e) => set("descripcion", e.currentTarget.value)}
                error={errors.descripcion}
              />
              <Select label="CeCo" options={cecos.map((c) => ({ value: c.id, label: c.codigo }))} searchable value={values.centroDeCostoId} onValueChange={(v) => set("centroDeCostoId", v)} error={errors.centroDeCostoId} />
              <Select label="Cuenta" options={cuentas.map((c) => ({ value: c.id, label: c.codigo }))} searchable value={values.cuentaContableId} onValueChange={(v) => set("cuentaContableId", v)} error={errors.cuentaContableId} />
            </div>
            <Group align="flex-end" gap="md">
              <NumberInput
                label="Importe"
                min={0}
                step={0.01}
                decimals={2}
                value={values.importe}
                onChange={(v) => set("importe", typeof v === "number" ? v : 0)}
                error={errors.importe}
                className="w-35"
              />
              <Select
                label="Indicador"
                options={indicadores.map((i) => ({ value: i.codigo, label: `${i.codigo} · ${(i.tasaOCuota * 100).toFixed(0)}%` }))}
                value={values.indicador}
                onValueChange={(v) => set("indicador", v)}
                error={errors.indicador}
                className="w-35"
              />
              <Button type="submit" leftSection={<IconPlus size={14} />} loading={agregar.isPending}>
                Agregar línea
              </Button>
            </Group>
          </Stack>
        </form>
      </Stack>
    </Card>
  );
}
